import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '../api/client';
import {
  createTestList,
  deleteTestList,
  listTestLists,
  listTests,
  runTestList,
} from '../api/endpoints';
import type { TestSummary } from '../api/types';
import { useResource, useToast } from '../state';
import { formatRelative, pluralize } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorNotice, LoadingRows } from '../components/Feedback';
import { ConfirmButton, Modal, TextField } from '../components/Form';
import { Mono, RunStatusPill } from '../components/Pills';

export function SuitesPage(): JSX.Element {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);

  const lists = useResource(() => listTestLists(), [], { live: ['test.updated', 'run.finished'] });

  const runSuite = async (id: string, name: string): Promise<void> => {
    setRunningId(id);
    try {
      const result = await runTestList(id, {});
      if (result.skipped.length > 0) {
        toast.info(
          `Started ${pluralize(result.runIds.length, 'run')} from “${name}”`,
          `skipped: ${result.skipped.map((entry) => `${entry.testId} (${entry.reason})`).join(', ')}`,
        );
      } else {
        toast.success(
          `Started ${pluralize(result.runIds.length, 'run')} from “${name}”`,
          result.creditsCharged === undefined
            ? 'results stream in live'
            : `${result.creditsCharged} credit(s) reserved · results stream in live`,
        );
      }
      lists.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Suites"
        subtitle="Named sets of tests that run together, matching what a CI job or PR check runs."
        actions={
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            New suite
          </button>
        }
      />

      {lists.error ? <ErrorNotice error={lists.error} onRetry={lists.reload} /> : null}
      {lists.loading ? <LoadingRows rows={3} label="Loading suites" /> : null}

      {!lists.loading && lists.data && lists.data.testlists.length === 0 ? (
        <EmptyState
          title="No suites yet"
          action={
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              Group your tests
            </button>
          }
        >
          <p>
            A suite pins an environment per case, so “run everything against staging” is one decision
            rather than a list of flags.
          </p>
        </EmptyState>
      ) : null}

      {lists.data && lists.data.testlists.length > 0 ? (
        <ul className="rows">
          {lists.data.testlists.map((list) => (
            <li key={list.id} className="row row--suite">
              <div className="row__main">
                <div className="row__title">
                  <strong>{list.name}</strong>
                  <span className="muted num">{pluralize(list.caseCount, 'case')}</span>
                </div>
                <ul className="suite-cases">
                  {list.cases.map((entry) => (
                    <li key={`${entry.testId}:${entry.environmentId ?? ''}`}>
                      {entry.latestStatus ? <RunStatusPill status={entry.latestStatus} /> : <span className="pill pill--neutral pill--subtle">never run</span>}
                      <Link className="linkish" to={`/projects/${entry.projectId ?? ''}?tab=runs`}>
                        {entry.testName}
                      </Link>
                      <span className="muted">{entry.environmentName}</span>
                    </li>
                  ))}
                </ul>
                <p className="row__meta">
                  <Mono>{list.id}</Mono>
                  <span>updated {formatRelative(list.updatedAt)}</span>
                </p>
              </div>
              <div className="row__actions">
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={runningId === list.id || list.caseCount === 0}
                  onClick={() => void runSuite(list.id, list.name)}
                >
                  {runningId === list.id ? 'Starting…' : 'Run suite'}
                </button>
                <ConfirmButton
                  label="Delete"
                  confirmLabel="Really delete?"
                  onConfirm={() => {
                    void deleteTestList(list.id)
                      .then(() => {
                        toast.success(`Deleted “${list.name}”`);
                        lists.reload();
                      })
                      .catch((error: unknown) => toast.error(describeError(error)));
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {creating ? <CreateSuiteDialog onClose={() => setCreating(false)} onSaved={() => lists.reload()} /> : null}
    </div>
  );
}

function CreateSuiteDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const tests = useResource(() => listTests(), []);

  const grouped = useMemo(() => {
    const byProject = new Map<string, TestSummary[]>();
    for (const test of tests.data?.tests ?? []) {
      const bucket = byProject.get(test.projectId) ?? [];
      bucket.push(test);
      byProject.set(test.projectId, bucket);
    }
    return [...byProject.entries()];
  }, [tests.data]);

  const toggle = (testId: string): void => {
    const next = new Set(selected);
    if (next.has(testId)) next.delete(testId);
    else next.add(testId);
    setSelected(next);
  };

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      await createTestList({
        name,
        cases: [...selected].map((testId) => ({ testId })),
      });
      toast.success(`Suite “${name}” created`, pluralize(selected.size, 'case'));
      onSaved();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      wide
      title="New suite"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={pending || !name || selected.size === 0}
          >
            {pending ? 'Creating…' : `Create suite (${selected.size})`}
          </button>
        </>
      }
    >
      <TextField label="Name" value={name} onChange={setName} required placeholder="Nightly smoke" />

      <h4 className="form-subhead">Cases</h4>
      {tests.loading ? <LoadingRows rows={3} label="Loading tests" /> : null}
      {grouped.length === 0 && !tests.loading ? (
        <p className="muted">No tests exist yet. Create one from a project first.</p>
      ) : null}

      {grouped.map(([projectId, projectTests]) => (
        <fieldset key={projectId} className="fieldset">
          <legend>
            <Link className="linkish" to={`/projects/${projectId}`}>
              {projectTests[0]?.name ?? projectId}
            </Link>
          </legend>
          {projectTests.map((test) => (
            <label key={test.id} className="checkbox">
              <input type="checkbox" checked={selected.has(test.id)} onChange={() => toggle(test.id)} />
              <span>{test.name}</span>
              {test.latestRun ? <RunStatusPill status={test.latestRun.status} /> : null}
            </label>
          ))}
        </fieldset>
      ))}

      {error ? <ErrorNotice error={error} /> : null}
    </Modal>
  );
}
